import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, XCircle, CreditCard, Package, Download, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import SHOP from "@/config/shop";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useSEO('Vysledek platby', `Stav vasi online platby na ${SHOP.domain}`);

  useEffect(() => {
    if (!sessionId) { setError('Chybi identifikator platby'); setLoading(false); return; }
    axios.get(`${API}/payments/status/${sessionId}`)
      .then(res => setStatus(res.data))
      .catch(() => setError('Nepodarilo se overit stav platby'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return (<div className="flex items-center justify-center min-h-[400px]" data-testid="payment-loading"><div className="text-center"><Loader2 className="w-10 h-10 animate-spin text-[#004E98] mx-auto mb-4" /><p className="text-slate-500">Overujeme stav platby...</p></div></div>);

  if (error) return (<div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="payment-error"><XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" /><h1 className="text-2xl font-bold text-slate-800 mb-2">Chyba platby</h1><p className="text-slate-500 mb-6">{error}</p><Link to="/kosik" className="inline-block bg-[#004E98] text-white hover:bg-[#003B73] rounded-sm px-6 py-3 font-medium text-sm">Zpet do kosiku</Link></div>);

  const isPaid = status?.payment_status === 'paid';
  const isPending = status?.payment_status === 'unpaid' || status?.payment_status === 'pending';

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="payment-success-page">
      <div className="text-center mb-8">
        {isPaid ? (
          <><div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-10 h-10 text-emerald-600" /></div><h1 className="text-2xl font-bold text-slate-800 mb-2">Platba probehla uspesne!</h1><p className="text-slate-500">Vase platba kartou byla prijata.</p></>
        ) : isPending ? (
          <><div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><CreditCard className="w-10 h-10 text-amber-600" /></div><h1 className="text-2xl font-bold text-slate-800 mb-2">Platba se zpracovava</h1><p className="text-slate-500">Vase platba se stale zpracovava.</p></>
        ) : (
          <><div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><XCircle className="w-10 h-10 text-red-500" /></div><h1 className="text-2xl font-bold text-slate-800 mb-2">Platba nebyla dokoncena</h1><p className="text-slate-500">Platba byla zrusena nebo se nezdarila.</p></>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <h2 className="font-semibold text-slate-800 mb-4">Detail platby</h2>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Stav platby</span><span className={`font-medium ${isPaid ? 'text-emerald-600' : isPending ? 'text-amber-600' : 'text-red-600'}`}>{isPaid ? 'Zaplaceno' : isPending ? 'Zpracovava se' : 'Nezaplaceno'}</span></div>
          {status?.amount && (<div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Castka</span><span className="font-bold text-[#004E98]">{(status.amount / 100).toFixed(2)} {status.currency?.toUpperCase()}</span></div>)}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {status?.order_id && (
          <>
            <Link to={`/objednavka/${status.order_id}`} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#004E98] text-white rounded-sm text-sm font-medium hover:bg-[#003B73] transition-colors" data-testid="view-order-btn"><Package className="w-4 h-4" />Zobrazit objednavku</Link>
            {isPaid && (<a href={`${API}/orders/${status.order_id}/invoice`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-[#004E98] text-[#004E98] rounded-sm text-sm font-medium hover:bg-[#004E98]/5 transition-colors" data-testid="download-invoice-btn"><Download className="w-4 h-4" />Stahnout fakturu (PDF)</a>)}
          </>
        )}
        <Link to="/produkty" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#FF6B35] text-white rounded-sm text-sm font-medium hover:bg-[#E85D2A] transition-colors" data-testid="continue-shopping-btn">Pokracovat v nakupu</Link>
      </div>
    </div>
  );
}
